import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  Search, Plus, FileText, Clock, Inbox, Loader2,
  ChevronLeft, ChevronRight, AlertCircle, Eye,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const STATUS_STYLE = {
  not_opened: "bg-slate-100 text-slate-600 border-slate-200",
  opened: "bg-blue-100 text-blue-600 border-blue-200",
  recommended: "bg-yellow-100 text-yellow-600 border-yellow-200",
  qc_passed: "bg-orange-100 text-orange-600 border-orange-200",
  qc_failed: "bg-red-100 text-red-600 border-red-200",
  approved: "bg-green-100 text-green-600 border-green-200",
  rejected: "bg-red-100 text-red-600 border-red-200",
  returned: "bg-purple-100 text-purple-600 border-purple-200",
  archived: "bg-slate-100 text-slate-500 border-slate-200",
};

const STATUS_LABEL = {
  not_opened: "Not Opened", opened: "Opened",
  recommended: "Recommended", qc_passed: "QC Passed",
  qc_failed: "QC Failed", approved: "Approved",
  rejected: "Rejected", returned: "Returned", archived: "Archived",
};

const ROLE_BADGE = {
  creator: "bg-slate-100 text-slate-600",
  recommender: "bg-amber-100 text-amber-600",
  qc: "bg-blue-100 text-blue-600",
  approver: "bg-purple-100 text-purple-600",
  viewer: "bg-app text-slate-400",
};

const FILTER_TABS = [
  { key: "all", label: "All Documents", icon: FileText },
  { key: "inbox", label: "Inbox", icon: Inbox },
  { key: "workflow", label: "Workflow", icon: Clock },
  { key: "drafts", label: "Drafts", icon: FileText },
  { key: "archived", label: "Archived", icon: FileText },
];

export default function SMSDashboard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [documents, setDocuments] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState(searchParams.get("filter") || "all");
  const [search, setSearch] = useState("");
  const [docType, setDocType] = useState("all");
  const [status, setStatus] = useState("all");
  const [offset, setOffset] = useState(0);
  const [docTypes, setDocTypes] = useState([]);
  const [stats, setStats] = useState(null);
  const limit = 50;

  useEffect(() => {
    const f = searchParams.get("filter");
    if (f) setFilterType(f);
  }, [searchParams]);

  useEffect(() => {
    fetchDocuments();
    if (filterType === "inbox" || filterType === "all") fetchStats();
  }, [filterType, docType, status, offset]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (search) fetchDocuments();
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    fetchDocTypes();
  }, []);

  const fetchDocTypes = async () => {
    try {
      const res = await axios.get(`${API}/sms/document-types`, {
        headers: getHeaders(), withCredentials: true,
      });
      setDocTypes(res.data.document_types || []);
    } catch {}
  };

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      const params = {
        filter_type: filterType,
        limit, offset,
      };
      if (search) params.search = search;
      if (docType && docType !== "all") params.doc_type = docType;
      if (status && status !== "all") params.status = status;

      const res = await axios.get(`${API}/sms/documents`, {
        params, headers: getHeaders(), withCredentials: true,
      });
      setDocuments(res.data.documents || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      toast.error("Failed to load documents");
    }
    setLoading(false);
  };

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/sms/stats`, {
        headers: getHeaders(), withCredentials: true,
      });
      setStats(res.data.stats);
    } catch {}
  };

  const totalPages = Math.ceil(total / limit);
  const currentPage = Math.floor(offset / limit) + 1;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="font-['Barlow_Condensed'] text-2xl font-bold text-slate-800 tracking-wide">
            Document Repository
          </h1>
          <p className="text-xs text-slate-500">Smart Management System</p>
        </div>
        <Button onClick={() => navigate("/hr-hub/smart-management/create")}
          className="rounded-none bg-emerald-600 hover:bg-emerald-500"
        >
          <Plus className="w-4 h-4 mr-1" /> New Document
        </Button>
      </div>

      {/* Stats bar */}
      {stats && (
        <div className="flex gap-3 mb-4 flex-wrap">
          <div className="px-3 py-2 bg-white border border-slate-200 text-xs">
            <span className="text-slate-500">Inbox: </span>
            <span className="font-semibold text-slate-800">{stats.inbox_count}</span>
          </div>
          <div className="px-3 py-2 bg-white border border-slate-200 text-xs">
            <span className="text-slate-500">Pending: </span>
            <span className="font-semibold text-amber-600">{stats.pending_actions}</span>
          </div>
          <div className="px-3 py-2 bg-white border border-slate-200 text-xs">
            <span className="text-slate-500">Workflow: </span>
            <span className="font-semibold text-slate-800">{stats.total_workflow}</span>
          </div>
          {stats.overdue_count > 0 && (
            <div className="px-3 py-2 bg-red-50 border border-red-200 text-xs">
              <span className="text-red-500">Overdue: </span>
              <span className="font-semibold text-red-600">{stats.overdue_count}</span>
            </div>
          )}
        </div>
      )}

      {/* Filter Tabs */}
      <div className="flex items-center gap-1 mb-3 border-b border-slate-200">
        {FILTER_TABS.map((tab) => {
          const TabIcon = tab.icon;
          return (
            <button key={tab.key} onClick={() => { setFilterType(tab.key); setOffset(0); }}
              className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium border-b-2 transition-colors ${
                filterType === tab.key
                  ? 'border-emerald-500 text-emerald-700 bg-emerald-50/50'
                  : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
              }`}
            >
              <TabIcon className="w-3.5 h-3.5" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Search & Filters */}
      <div className="flex items-center gap-2 mb-4">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
          <Input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search by ID or name..." className="pl-8 h-8 text-xs rounded-none border-slate-300"
          />
        </div>
        <Select value={docType} onValueChange={setDocType}>
          <SelectTrigger className="w-36 h-8 text-xs rounded-none bg-white border-slate-300">
            <SelectValue placeholder="All types" />
          </SelectTrigger>
          <SelectContent className="bg-white border-slate-300 rounded-none">
            <SelectItem value="all">All types</SelectItem>
            {docTypes.map(dt => (
              <SelectItem key={dt.id} value={dt.code}>{dt.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-32 h-8 text-xs rounded-none bg-white border-slate-300">
            <SelectValue placeholder="All status" />
          </SelectTrigger>
          <SelectContent className="bg-white border-slate-300 rounded-none">
            <SelectItem value="all">All status</SelectItem>
            {Object.entries(STATUS_LABEL).map(([k, v]) => (
              <SelectItem key={k} value={k}>{v}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" className="h-8 text-xs rounded-none"
          onClick={() => { setSearch(""); setDocType("all"); setStatus("all"); setOffset(0); }}
        >
          Clear
        </Button>
      </div>

      {/* Document Table */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-emerald-500" />
        </div>
      ) : documents.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 p-12 text-center">
          <FileText className="w-10 h-10 text-slate-300 mx-auto mb-2" />
          <p className="text-sm text-slate-500">No documents found</p>
          <Button size="sm" className="mt-3 rounded-none bg-emerald-600 hover:bg-emerald-500"
            onClick={() => navigate("/hr-hub/smart-management/create")}
          >
            <Plus className="w-3.5 h-3.5 mr-1" /> Create your first document
          </Button>
        </div>
      ) : (
        <>
          <div className="bg-white border border-slate-200 overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-app border-b border-slate-200">
                  <th className="text-left px-3 py-2 font-medium text-slate-500 uppercase">Doc ID</th>
                  <th className="text-left px-3 py-2 font-medium text-slate-500 uppercase">Name</th>
                  <th className="text-left px-3 py-2 font-medium text-slate-500 uppercase">Type</th>
                  <th className="text-left px-3 py-2 font-medium text-slate-500 uppercase">Status</th>
                  <th className="text-left px-3 py-2 font-medium text-slate-500 uppercase">Expected By</th>
                  <th className="text-left px-3 py-2 font-medium text-slate-500 uppercase">Role</th>
                  <th className="text-center px-3 py-2 font-medium text-slate-500 uppercase">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {documents.map((doc) => {
                  const isOverdue = doc.days_remaining !== null && doc.days_remaining < 0
                    && !["approved", "rejected", "archived"].includes(doc.status);
                  const canAct = ["recommender", "qc", "approver"].includes(doc.my_role)
                    && ["not_opened", "opened", "recommended", "qc_passed", "qc_failed"].includes(doc.status);

                  return (
                    <tr key={doc.id}
                      className="hover:bg-app cursor-pointer transition-colors"
                      onClick={() => navigate(`/hr-hub/smart-management/${doc.id}`)}
                    >
                      <td className="px-3 py-2.5 font-mono text-xs text-slate-700">{doc.doc_id}</td>
                      <td className="px-3 py-2.5 text-slate-800 font-medium max-w-[200px] truncate">
                        {doc.name}
                      </td>
                      <td className="px-3 py-2.5 text-slate-600">{doc.doc_type_code}</td>
                      <td className="px-3 py-2.5">
                        <Badge variant="outline"
                          className={`text-[10px] px-1.5 py-0 font-medium border ${STATUS_STYLE[doc.status] || STATUS_STYLE.not_opened}`}
                        >
                          {isOverdue && <AlertCircle className="w-3 h-3 mr-1 text-red-500" />}
                          {STATUS_LABEL[doc.status] || doc.status}
                        </Badge>
                      </td>
                      <td className="px-3 py-2.5">
                        {doc.expected_action_date ? (
                          <span className={`text-xs ${isOverdue ? 'text-red-500 font-semibold' : 'text-slate-500'}`}>
                            {doc.days_remaining !== null
                              ? (doc.days_remaining >= 0
                                ? `${doc.days_remaining}d remaining`
                                : `${Math.abs(doc.days_remaining)}d overdue`)
                              : new Date(doc.expected_action_date).toLocaleDateString()
                            }
                          </span>
                        ) : (
                          <span className="text-slate-300">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2.5">
                        <span className={`text-[10px] px-1.5 py-0.5 rounded ${ROLE_BADGE[doc.my_role] || ROLE_BADGE.viewer}`}>
                          {doc.my_role}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 text-center">
                        {canAct ? (
                          <Button size="sm" variant="default"
                            className="h-6 text-[10px] px-2 rounded-none bg-emerald-600 hover:bg-emerald-500"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/hr-hub/smart-management/${doc.id}`);
                            }}
                          >
                            <Eye className="w-3 h-3 mr-1" /> Action
                          </Button>
                        ) : (
                          <Button size="sm" variant="ghost"
                            className="h-6 text-[10px] px-2 rounded-none text-slate-400"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/hr-hub/smart-management/${doc.id}`);
                            }}
                          >
                            View
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-3 text-xs text-slate-500">
              <span>{total} document{total !== 1 ? 's' : ''}</span>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" className="h-7 w-7 p-0 rounded-none"
                  disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - limit))}
                >
                  <ChevronLeft className="w-3.5 h-3.5" />
                </Button>
                <span>Page {currentPage} of {totalPages}</span>
                <Button variant="outline" size="sm" className="h-7 w-7 p-0 rounded-none"
                  disabled={offset + limit >= total}
                  onClick={() => setOffset(offset + limit)}
                >
                  <ChevronRight className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
